import {Component, ComponentFactoryResolver, Inject, OnInit} from '@angular/core';
import {MatDialogRef, MAT_DIALOG_DATA} from '@angular/material/dialog';
import {RailgunBase} from 'src/app/RailgunBase';
import {MessageTemplateService} from 'src/app/Service/MessageTemplateService';
import {MailTemplateDTOInterface} from 'src/app/DTO/MailTemplateDTOInterface';
import {MailTemplateCategoryDTOInterface} from 'src/app/DTO/MailTemplateCategoryDTOInterface';

/**
 * MessageTemplateCreatorPopoverComponent
 * メッセージテンプレート作成・編集ポップオーバー
 *
 * 呼び出し方：
 * this.showDialogWindow(
 *   MessageTemplateCreatorPopoverComponent,
 *   {
 *     mode: 'create', // または 'edit'
 *     template: existingTemplate, // 編集時のみ
 *     close: this.onTemplateCreated.bind(this)
 *   }
 * );
 *
 * closeメソッドは以下の引数で用意する：
 * onTemplateCreated(result: { success: boolean, template?: MailTemplateDTOInterface })
 */
@Component({
	selector: 'message-template-creator-popover',
	templateUrl: './MessageTemplateCreatorPopoverComponent.html',
	styleUrls: ['./MessageTemplateCreatorPopoverComponent.scss']
})
export class MessageTemplateCreatorPopoverComponent extends RailgunBase implements OnInit {

	public isLoading = false;
	public isEditMode = false;

	// フォームデータ
	public formData: MailTemplateDTOInterface = {
		id: 0,
		name: '',
		subject: '',
		content: '',
		mailTemplateCategory: []
	};

	// エラーメッセージ
	public errorMessages: string[] = [];

	// MessageTemplateServiceのインスタンス
	private messageTemplateService: MessageTemplateService;

	constructor(
		public resolver: ComponentFactoryResolver,
		public dialogRef: MatDialogRef<MessageTemplateCreatorPopoverComponent>,
		@Inject(MAT_DIALOG_DATA) public data: any
	) {
		super();
		this.isEditMode = data.mode === 'edit';
		this.messageTemplateService = new MessageTemplateService(this);
	}

	/**
	 * ngOnInit
	 * Angular標準のライフサイクルフック
	 */
	ngOnInit(): void {
		this.start();
	}

	/**
	 * start
	 * 初期化処理を実行
	 */
	public async start() {
		await this.initializeForm();
	}

	/**
	 * フォームの初期化
	 */
	private async initializeForm() {
		this.isLoading = true;

		if (this.isEditMode && this.data.template) {
			// 編集モードの場合、既存データを取得
			const template = await this.messageTemplateService.getMailTemplate(this.data.template.id);
			if (template) {
				this.formData = {...template};
			} else {
				this.errorMessages = ['テンプレートデータの取得に失敗しました。'];
			}
		} else {
			// 新規作成モードの場合、空のテンプレートを取得（カテゴリ情報含む）
			const template = await this.messageTemplateService.getMailTemplate(0);
			if (template?.mailTemplateCategory) {
				this.formData = {
					id: 0,
					name: '',
					subject: '',
					content: '',
					mailTemplateCategory: template.mailTemplateCategory
				};
			} else {
				// カテゴリ取得に失敗しても、基本的なフォームは表示する
				this.formData = {
					id: 0,
					name: '',
					subject: '',
					content: '',
					mailTemplateCategory: []
				};
				this.errorMessages = ['カテゴリ情報の取得に失敗しました。'];
			}
		}

		// 必ずローディングを終了する
		this.isLoading = false;
	}

	/**
	 * 入力値検証
	 */
	private validateInput(): string[] {
		const errors: string[] = [];

		if (!this.formData.name || this.formData.name.trim().length === 0) {
			errors.push('テンプレート名が入力されていません。');
		}

		if (!this.formData.subject || this.formData.subject.trim().length === 0) {
			errors.push('件名が入力されていません。');
		}

		if (!this.formData.content || this.formData.content.trim().length === 0) {
			errors.push('本文が入力されていません。');
		}

		return errors;
	}

	/**
	 * 保存処理
	 */
	public async onSave() {
		this.isLoading = true;
		this.errorMessages = [];

		// 入力値検証
		const validationErrors = this.validateInput();
		if (validationErrors.length > 0) {
			this.errorMessages = validationErrors;
			this.isLoading = false;
			return;
		}

		// 選択されたカテゴリのみをフィルタ
		const saveData = {...this.formData};
		if (saveData.mailTemplateCategory && Array.isArray(saveData.mailTemplateCategory)) {
			saveData.mailTemplateCategory = saveData.mailTemplateCategory.filter(
				(category: MailTemplateCategoryDTOInterface) => category.checked
			);
		}

		let resultTemplate: MailTemplateDTOInterface | null = null;

		if (this.isEditMode && saveData.id > 0) {
			// 編集処理
			resultTemplate = await this.messageTemplateService.editMailTemplate(saveData);
		} else {
			// 新規作成処理
			resultTemplate = await this.messageTemplateService.addMailTemplate(saveData);
		}

		if (resultTemplate) {
			// 成功時は結果を返して閉じる
			const result = {
				success: true,
				template: resultTemplate
			};
			this.data.close(result);
		} else {
			// 失敗時はエラーメッセージを表示
			this.errorMessages = ['保存に失敗しました。'];
		}

		// ローディング状態を終了
		this.isLoading = false;
	}

	/**
	 * キャンセル処理
	 */
	public onCancel() {
		const result = {
			success: false
		};
		this.data.close(result);
	}

	/**
	 * ダイアログのタイトルを取得
	 */
	public getDialogTitle(): string {
		return this.isEditMode ? 'メッセージテンプレート編集' : 'メッセージテンプレート作成';
	}

	/**
	 * 保存ボタンのラベルを取得
	 */
	public getSaveButtonLabel(): string {
		return this.isEditMode ? '更新' : '作成';
	}
}
